import { CornerDownLeft, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { filtrarMenu, usePodeVer } from './menu';

/**
 * Busca rápida, aberta com Ctrl/⌘ K de qualquer tela.
 *
 * O Adonai busca tela OU número de etiqueta de capa — não temos "capa" nem
 * "etiqueta", então a busca aqui é só por tela.
 */
export function PaletaComandos({
  aberta,
  aoFechar,
}: {
  aberta: boolean;
  aoFechar: () => void;
}) {
  const [texto, setTexto] = useState('');
  const [indice, setIndice] = useState(0);
  const refEntrada = useRef<HTMLInputElement>(null);
  const podeVer = usePodeVer();
  const navegar = useNavigate();

  const alvo = texto.trim();

  // A busca por Ctrl+K é outra porta para o mesmo menu: filtrar só a barra
  // lateral deixaria a tela escondida acessível por atalho.
  const opcoes = useMemo(() => {
    return filtrarMenu(alvo, podeVer).flatMap((grupo) =>
      grupo.itens.map((item) => ({
        chave: item.para,
        titulo: item.rotulo,
        contexto: grupo.titulo,
        destino: item.para,
      })),
    );
  }, [alvo, podeVer]);

  // Recomeça do topo a cada tecla: manter o índice antigo selecionaria um item
  // que já não está na mesma posição, e o Enter abriria a tela errada.
  useEffect(() => setIndice(0), [texto]);

  useEffect(() => {
    if (!aberta) return;
    setTexto('');
    refEntrada.current?.focus();
  }, [aberta]);

  if (!aberta) return null;

  const escolher = (destino: string) => {
    aoFechar();
    navegar(destino);
  };

  const aoTeclar = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') return aoFechar();
    if (opcoes.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndice((i) => (i + 1) % opcoes.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndice((i) => (i - 1 + opcoes.length) % opcoes.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const escolhida = opcoes[indice];
      if (escolhida) escolher(escolhida.destino);
    }
  };

  return (
    <div
      className="bg-palco/40 fixed inset-0 z-[70] flex items-start justify-center px-4 pt-[12vh] backdrop-blur-[2px]"
      onClick={aoFechar}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Busca rápida"
        onClick={(e) => e.stopPropagation()}
        className="bg-superficie border-tinta-100 animate-surgir w-full max-w-lg overflow-hidden rounded-xl border shadow-2xl"
      >
        <div className="border-tinta-100 flex items-center gap-3 border-b px-4">
          <Search className="text-tinta-500 size-4 shrink-0" aria-hidden />
          <input
            ref={refEntrada}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={aoTeclar}
            placeholder="Buscar tela…"
            aria-label="Buscar tela"
            className="text-tinta-900 placeholder:text-tinta-500 flex-1 bg-transparent py-3.5 text-sm outline-none"
          />
          <kbd className="border-tinta-100 text-tinta-500 hidden rounded border px-1.5 py-0.5 text-[10px] font-medium sm:inline">
            Esc
          </kbd>
        </div>

        <ul className="rolagem-fina max-h-80 overflow-y-auto p-1.5">
          {opcoes.map((opcao, i) => (
            <li key={opcao.chave}>
              <button
                type="button"
                onClick={() => escolher(opcao.destino)}
                onMouseEnter={() => setIndice(i)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                  i === indice ? 'bg-marca-100 text-marca-600' : 'text-tinta-700'
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">
                    {opcao.titulo}
                  </span>
                  <span className="text-tinta-500 block text-xs">{opcao.contexto}</span>
                </span>
                {i === indice && (
                  <CornerDownLeft className="size-3.5 shrink-0" aria-hidden />
                )}
              </button>
            </li>
          ))}

          {opcoes.length === 0 && (
            <li className="text-tinta-500 px-3 py-8 text-center text-sm">
              Nada encontrado para “{alvo}”.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
